// Where every tile on the table is (design 10 §3): a pure function from the seat view and the table's
// measured anchors to each tile's centre, angle, width and look. The table moves each tile element there by
// its transform alone, so nothing on the table lays out or repaints when a tile moves.
//
// Rivers keep a fixed depth, as most clients draw them: rows of six, the third row running on, so a seat's
// area never grows and its hand never moves.

import { ActionType } from "../pb/mj/record/v1/record_pb";
import type { Look } from "../ui/tileArt";
import { NO_TILE } from "./tiles";
import type { Meld, SeatView, View } from "./view";
import { FIRST_LIVE, SEATS } from "./view";
import { STACKS, dice, wallSpot } from "./wall";

/** A seat's frame on the board: a point (u along the seat's rows, v away from the centre) is at
 *  (ox + ux·u + vx·v, oy + uy·u + vy·v) in the layer's pixels. */
export interface Frame {
  ox: number;
  oy: number;
  ux: number;
  uy: number;
  vx: number;
  vy: number;
}

export interface SeatGeometry {
  frame: Frame;
  river: { u: number; v: number; t: number; gap: number }; // the river's top-left in the frame, its tile width
  hand: { u: number; v: number; t: number }; // another seat's hand row: its top-left and tile width
  wall: { v: number; t: number }; // the seat's side of the wall: its row's top in the frame, a stack's width
}

export interface OwnGeometry {
  cx: number; // the hand row's centre and bottom, in the layer's pixels
  bottom: number;
  t: number;
  gap: number; // between the hand's tiles
  drawnGap: number; // before the drawn tile's place
  meldT: number;
  meldGap: number; // before the melds
}

export interface Geometry {
  seats: SeatGeometry[]; // by place at the table: 0 self, 1 right, 2 across, 3 left
  own: OwnGeometry;
  center: { x: number; y: number }; // the board's centre: the walls are centred on it
  lean?: number; // the camera's lean, degrees (design 10 T15): standing tiles rise into what lies beyond them
}

export type Where = "hand" | "drawn" | "river" | "meld" | "wall";

export interface Pose {
  x: number;
  y: number;
  angle: number;
  width: number;
  rise?: number; // its base's height over the felt, in tile widths: a wall's upper tile, a tile lying on a hand
}

export interface Placement {
  key: string; // the physical tile: its wall index when known (design 10 §3)
  tile: number; // its face, NO_TILE when face down
  x: number; // centre, in the layer's pixels
  y: number;
  angle: number; // degrees
  width: number; // the tile's width in pixels
  look: Look;
  z: number;
  seat: number;
  where: Where;
  index: number; // its slot, river place or meld tile
  home?: Pose; // a dealt tile's place in the wall, where the deal takes it from (design 10 §4)
  rise?: number; // its base's height over the felt, in tile widths: a wall's upper tile, a tile lying on a hand
  screen?: boolean; // the own row: upright on the screen, however the board leans (design 10 §6)
  stand?: boolean; // another seat's concealed tile: it stands on its edge, facing its owner
  deal?: number; // its place in the deal's order: blocks of four, then one each
}

/** A tile as made for Japanese automatic tables, the size most riichi is played with (Taiyo's BN tile for the
 *  AMOS tables: 28 mm tall, 20.5 mm wide, 16 mm thick; design 10 T16). Every tile in the game has its
 *  proportions; styles.css repeats them (--tile-h). */
export const TILE_MM = { width: 20.5, height: 28, depth: 16 };
export const H = TILE_MM.height / TILE_MM.width; // a tile's height over its width
export const DEPTH = TILE_MM.depth / TILE_MM.width; // its thickness over its width

/** A tile's key: its wall index when the view follows it, else its face, else its seat and slot. */
function keyOf(v: View, tile: number, fallback: string): string {
  const wall = tile !== NO_TILE ? v.wallOf[tile] : undefined;
  if (wall !== undefined && wall >= 0) return `w${wall}`;
  return tile !== NO_TILE ? `t${tile}` : fallback;
}

const angleOf = (f: Frame) => (Math.atan2(f.uy, f.ux) * 180) / Math.PI;
const at = (f: Frame, u: number, v: number) => ({ x: f.ox + f.ux * u + f.vx * v, y: f.oy + f.uy * u + f.vy * v });

/** A row of tiles left to right from u0, bottoms on `bottom`: each item's centre (u, v) and whether it lies
 *  sideways (taking a tile's height of the row). */
function row(items: readonly { sideways: boolean }[], u0: number, bottom: number, t: number, gap: number): { u: number; v: number }[] {
  let u = u0;
  return items.map((it) => {
    const w = it.sideways ? t * H : t;
    const c = { u: u + w / 2, v: bottom - (it.sideways ? t : t * H) / 2 };
    u += w + gap;
    return c;
  });
}

/** A meld's tiles in table order: the called tile turned sideways at the place of the seat it came from
 *  (left, across or right); a closed kan's outer tiles face down. */
export function meldTiles(meld: Meld, owner: number): { tile: number; sideways: boolean; down: boolean }[] {
  const closed = meld.type === ActionType.CLOSED_KAN;
  if (closed) return meld.tiles.map((t, i) => ({ tile: t, sideways: false, down: i === 0 || i === 3 }));
  const rel = (meld.from - owner + SEATS) % SEATS;
  const rest = meld.tiles.filter((t) => t !== meld.called);
  const pos = rel === 3 ? 0 : rel === 2 ? 1 : rest.length;
  const out = rest.map((t) => ({ tile: t, sideways: false, down: false }));
  if (meld.called !== NO_TILE) out.splice(pos, 0, { tile: meld.called, sideways: true, down: false });
  return out;
}

export interface OwnHand {
  tiles: readonly number[]; // the own concealed tiles in the order shown
  dim: (tile: number) => boolean; // a tile that cannot be played now
}

/** Every tile on the table, from the view, the own hand's order and the measured geometry. */
export function place(v: View, own: OwnHand, g: Geometry): Placement[] {
  const out: Placement[] = [];
  for (let rel = 0; rel < SEATS; rel++) {
    const seat = (v.seat + rel) % SEATS;
    const s = v.seats[seat];
    const sg = g.seats[rel];
    if (!s || !sg) continue;
    river(v, s, seat, sg, out);
    if (rel === 0) ownRow(v, s, seat, own, g.own, out);
    else otherHand(v, s, seat, sg, g.center, out, g.lean ?? 0);
  }
  wall(v, g, out);
  homes(v, g, out);
  return out;
}

/** Where wall index `index` stands on the table, in the layer's pixels. */
export function wallPose(v: View, g: Geometry, index: number): Pose | null {
  if (!v.hand) return null;
  const [a, b] = dice(v.gameId, v.hand.index);
  const spot = wallSpot(index, v.hand.dealer, a + b);
  const sg = g.seats[(spot.seat - v.seat + SEATS) % SEATS];
  if (!sg?.wall.t) return null;
  const f = sg.frame;
  const t = sg.wall.t;
  // The row is centred on the board's centre; a side's stacks run from its owner's right (k = 0) to its
  // left, two tiles high.
  const uc = (g.center.x - f.ox) * f.ux + (g.center.y - f.oy) * f.uy;
  const u = uc + ((STACKS - 1) / 2 - spot.k) * t;
  return { ...at(f, u, sg.wall.v + (t * H) / 2), angle: angleOf(f), width: t, rise: spot.top ? DEPTH : 0 };
}

const DEAD = 122; // the record's dead wall starts here: replacements 122–125, the indicators 126–135

/** The tiles still in the wall: the live wall not yet drawn, the replacements not yet taken, the indicator
 *  stacks (a revealed indicator face up). */
function wall(v: View, g: Geometry, out: Placement[]): void {
  const h = v.hand;
  if (!h) return;
  const shown = new Map<number, number>(); // wall index: the revealed indicator's face
  for (const tile of [...h.indicators, ...h.ura]) {
    const w = v.wallOf[tile];
    if (w !== undefined) shown.set(w, tile);
  }
  for (let i = 0; i < 136; i++) {
    const inWall = (i >= h.nextLive && i < DEAD) || (i >= DEAD && i < DEAD + 4 && i >= DEAD + h.replacements) || i >= DEAD + 4;
    if (!inWall) continue;
    const pose = wallPose(v, g, i);
    if (!pose) return;
    const spot = wallSpot(i, h.dealer, dice(v.gameId, h.index).reduce((x, y) => x + y));
    out.push({ key: `w${i}`, tile: shown.get(i) ?? NO_TILE, ...pose, look: "plain", z: spot.top ? 2 : 1, seat: spot.seat, where: "wall", index: i });
  }
}

/** Gives every dealt tile its home in the wall, and its place in the deal's order. */
function homes(v: View, g: Geometry, out: Placement[]): void {
  for (const p of out) {
    const m = /^w(\d+)$/.exec(p.key);
    if (!m || p.where === "wall") continue;
    const i = Number(m[1]);
    const home = wallPose(v, g, i);
    if (home) p.home = home;
    if (i < FIRST_LIVE) p.deal = i < 48 ? Math.floor(i / 4) : 12 + (i - 48);
  }
}

function river(v: View, s: SeatView, seat: number, g: SeatGeometry, out: Placement[]): void {
  const f = g.frame;
  const { t, gap } = g.river;
  const shown = s.river.filter((r) => !r.calledAway); // a called tile has gone to its meld
  const rows: (typeof shown)[] = [shown.slice(0, 6), shown.slice(6, 12), shown.slice(12)];
  let index = 0;
  rows.forEach((tiles, r) => {
    const bottom = g.river.v + (r + 1) * t * H + r * gap;
    const cells = row(tiles.map((x) => ({ sideways: x.riichi })), g.river.u, bottom, t, gap);
    // The third row runs on past its six only by closing up: run straight on, it lay over a neighbour's score (UX review 2026-11-02). Each tile still shows its left.
    const first = cells[0]?.u ?? 0;
    const end = g.river.u + 5.5 * t + 5 * gap;
    const close = r === 2 && tiles.length > 6 ? (end - first) / (cells[cells.length - 1]!.u - first) : 1;
    cells.forEach((c, i) => {
      const x = tiles[i]!;
      const p = at(f, first + (c.u - first) * close, c.v);
      out.push({
        key: keyOf(v, x.tile, `r${seat}-${index}`),
        tile: x.tile,
        ...p,
        angle: angleOf(f) + (x.riichi ? -90 : 0),
        width: t,
        look: x.tsumogiri ? "dark" : "plain",
        z: 10 + index,
        seat,
        where: "river",
        index: index++,
      });
    });
  });
}

function otherHand(v: View, s: SeatView, seat: number, g: SeatGeometry, center: { x: number; y: number }, out: Placement[], lean: number): void {
  const first = out.length;
  // The row starts where the corner on its owner's left begins: as far left of the seat's middle as the row
  // is out from the centre. The four rows turn like a pinwheel, each running on into the corner on its
  // owner's right (its melds), never into another's (design 10 T17).
  const f = g.frame;
  const uc = (center.x - f.ox) * f.ux + (center.y - f.oy) * f.uy;
  const vc = (center.x - f.ox) * f.vx + (center.y - f.oy) * f.vy;
  otherHandTiles(v, s, seat, { ...g, hand: { ...g.hand, u: uc - (g.hand.v - vc) } }, out, lean);
  // Concealed tiles stand on their edge, as at a table; a hand laid down at the hand's end lies flat. A drawn
  // tile laid on top of the hand lies across the standing tiles' tops. The melds lie flat.
  const laidDown = s.shown !== null || s.known;
  const onTop = v.players[seat]?.drawnOnTop ?? false;
  for (let i = first; i < out.length; i++) {
    const p = out[i]!;
    if (p.where === "hand" || (p.where === "drawn" && !onTop)) p.stand = !laidDown;
    else if (p.where === "drawn" && !laidDown) {
      const row = out.filter((q, j) => j >= first && q.where === "hand");
      const a = row[0];
      const b = row[row.length - 1];
      if (a && b) Object.assign(p, { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, rise: H });
    }
  }
}

function otherHandTiles(v: View, s: SeatView, seat: number, g: SeatGeometry, out: Placement[], leanDeg: number): void {
  const f = g.frame;
  const { t } = g.hand;
  const bottom = g.hand.v + t * H;
  const angle = angleOf(f);
  // Face up (a replay showing every hand) the tiles are the view's, sorted; face down, the slots. A hand laid
  // down at the hand's end shows its faces in its own slots, the winning tile kept apart.
  const faces = s.shown ? s.shown.filter((t) => t !== s.winning) : null;
  const tiles = s.known ? s.concealed.map((tile) => ({ tile, wall: -1 })) : s.slotWalls.map((wall, i) => ({ tile: faces?.[i] ?? NO_TILE, wall }));
  const fallback = (i: number) => (s.known ? undefined : `h${seat}-${i}`);
  const hidden = s.known ? 0 : Math.max(0, s.hidden - (s.apart ? 1 : 0) - tiles.length); // slots the view did not follow
  for (let i = 0; i < hidden; i++) tiles.push({ tile: NO_TILE, wall: -1 });
  row(tiles.map(() => ({ sideways: false })), g.hand.u, bottom, t, 0).forEach((c, i) => {
    const x = tiles[i]!;
    out.push({
      key: x.wall >= 0 ? `w${x.wall}` : keyOf(v, x.tile, fallback(i) ?? `h${seat}-${i}`),
      tile: x.tile,
      ...at(f, c.u, c.v),
      angle,
      width: t,
      look: "plain",
      z: 5,
      seat,
      where: "hand",
      index: i,
    });
  });
  let u = g.hand.u + tiles.length * t;
  const apart = s.known ? s.drawn !== NO_TILE : s.apart;
  const onTop = v.players[seat]?.drawnOnTop ?? false;
  if (apart) {
    const tile = s.known ? s.drawn : s.shown && s.winning !== NO_TILE ? s.winning : NO_TILE;
    // Apart at the right, or lying sideways on top of the hand's right end, as some play over the board.
    const c = onTop ? { u: (g.hand.u + u) / 2, v: g.hand.v - t / 2 - 0.1 * t } : { u: u + 0.3 * t + t / 2, v: bottom - (t * H) / 2 };
    if (!onTop) u += 0.3 * t;
    out.push({
      key: !s.known && s.drawnWall >= 0 ? `w${s.drawnWall}` : keyOf(v, tile, `d${seat}`),
      tile,
      ...at(f, c.u, c.v),
      angle: angle + (onTop ? -90 : 0),
      width: t,
      look: "plain",
      z: 5,
      seat,
      where: "drawn",
      index: 0,
    });
    if (!onTop) u += t;
  }
  // A row that runs away from the camera (a side seat's, upward on the screen) puts its melds beyond the
  // standing tiles, which rise towards them as the board leans: the gap clears the standing tiles' projected
  // height (their height times the lean's tangent; audit 2026-10-14: at 60° a meld lay under the hand).
  const far = f.uy < -0.5;
  const clear = far && !(s.shown !== null || s.known) ? Math.max(0.3 * t, H * t * Math.tan((leanDeg * Math.PI) / 180)) : 0.3 * t;
  melds(v, s, seat, f, u + clear, bottom, t, angle, out);
}

function ownRow(v: View, s: SeatView, seat: number, own: OwnHand, g: OwnGeometry, out: Placement[]): void {
  const first = out.length;
  ownRowTiles(v, s, seat, own, g, out);
  for (let i = first; i < out.length; i++) out[i]!.screen = true;
}

function ownRowTiles(v: View, s: SeatView, seat: number, own: OwnHand, g: OwnGeometry, out: Placement[]): void {
  const { t, gap } = g;
  // The drawn tile's place is kept when empty, so the hand never moves as tiles are drawn and discarded;
  // laid on top of the hand (design 10 T6), it needs no place in the row.
  const onTop = v.players[seat]?.drawnOnTop ?? false;
  const meldItems = s.melds.map((m) => meldTiles(m, seat));
  const meldWidth = meldItems.reduce((w, m) => w + m.reduce((a, x) => a + (x.sideways ? g.meldT * H : g.meldT), 0), 0);
  const handWidth = own.tiles.length * t + Math.max(0, own.tiles.length - 1) * gap + (onTop ? 0 : g.drawnGap + t);
  const total = handWidth + (meldItems.length ? g.meldGap + meldWidth + (meldItems.length - 1) * g.meldGap * 0.5 : 0);
  let u = g.cx - total / 2;
  const y = g.bottom - (t * H) / 2;
  own.tiles.forEach((tile, i) => {
    out.push({ key: keyOf(v, tile, `t${tile}`), tile, x: u + t / 2, y, angle: 0, width: t, look: own.dim(tile) ? "dim" : "plain", z: 50, seat, where: "hand", index: i });
    u += t + gap;
  });
  if (onTop) {
    const left = g.cx - total / 2;
    u -= gap;
    if (s.drawn !== NO_TILE) {
      const x = (left + u) / 2; // over the middle of the hand
      out.push({ key: keyOf(v, s.drawn, `t${s.drawn}`), tile: s.drawn, x, y: y - (t * H) / 2 - t / 2 - 0.1 * t, angle: -90, width: t, look: own.dim(s.drawn) ? "dim" : "plain", z: 55, seat, where: "drawn", index: 0 });
    }
  } else {
    u += g.drawnGap - gap;
    if (s.drawn !== NO_TILE) {
      out.push({ key: keyOf(v, s.drawn, `t${s.drawn}`), tile: s.drawn, x: u + t / 2, y, angle: 0, width: t, look: own.dim(s.drawn) ? "dim" : "plain", z: 50, seat, where: "drawn", index: 0 });
    }
    u += t;
  }
  if (!meldItems.length) return;
  const frame: Frame = { ox: 0, oy: 0, ux: 1, uy: 0, vx: 0, vy: 1 };
  melds(v, s, seat, frame, u + g.meldGap, g.bottom, g.meldT, 0, out, g.meldGap * 0.5);
}

function melds(v: View, s: SeatView, seat: number, f: Frame, u0: number, bottom: number, t: number, angle: number, out: Placement[], gap = 0.15 * t): void {
  let u = u0;
  let index = 0;
  s.melds.forEach((m, mi) => {
    const items = meldTiles(m, seat);
    const centres = row(items, u, bottom, t, 0);
    items.forEach((it, i) => {
      const c = centres[i]!;
      out.push({
        key: keyOf(v, it.tile, `m${seat}-${mi}-${i}`),
        tile: it.down ? NO_TILE : it.tile,
        ...at(f, c.u, c.v),
        angle: angle + (it.sideways ? -90 : 0),
        width: t,
        look: "plain",
        z: 40,
        seat,
        where: "meld",
        index: index++,
      });
    });
    u += items.reduce((w, it) => w + (it.sideways ? t * H : t), 0) + gap;
  });
}
